import LegalLayout from "@/components/legal/LegalLayout";
import { useI18n } from "@/lib/i18n";
import { CONTACT_EMAIL } from "@/data/landing";

export default function Privacy() {
  const { t, lang } = useI18n();
  return (
    <LegalLayout
      title={t("privacy.title")}
      kicker={t("privacy.kicker")}
      lastUpdated={t("privacy.lastUpdated")}
    >
      {lang === "tr" ? <TrContent /> : <EnContent />}
    </LegalLayout>
  );
}

function EnContent() {
  return (
    <>
      <p>
        This Privacy Policy describes how Health Break (&ldquo;we&rdquo;, &ldquo;us&rdquo;,
        &ldquo;our&rdquo;) collects, uses and protects your information when you use the
        Health Break mobile application and related services (the &ldquo;Service&rdquo;).
        By using Health Break you agree to this policy. If you don&rsquo;t agree, please
        don&rsquo;t use the app.
      </p>

      <h2>1. Information we collect</h2>
      <h3>Account information</h3>
      <p>
        When you sign up using Apple, Google or email + password, we collect a basic
        identifier (name and email when available) so we can create your account. If you
        sign in with Apple, you can choose to hide your real email and Apple will provide
        a relay address — we treat that the same way.
      </p>
      <h3>Wellness preferences</h3>
      <ul>
        <li>Work hours, focus blocks and break preferences you set during onboarding.</li>
        <li>Body areas you&rsquo;ve selected (e.g. neck, lower back, wrists).</li>
        <li>Sessions you&rsquo;ve completed, skipped or replayed.</li>
        <li>How long you&rsquo;ve been working at your desk during the day.</li>
      </ul>
      <h3>Device information</h3>
      <p>
        We collect minimal technical data needed to keep the app stable: device type,
        operating system version, app version, language and approximate timezone.
      </p>

      <h2>2. How we use your information</h2>
      <ul>
        <li>To deliver the core experience: timely 1-minute breaks, reminders and weekly insights.</li>
        <li>To personalise sessions to the body areas you care about.</li>
        <li>To improve the app — diagnose crashes, fix bugs, and prioritise new features.</li>
        <li>To send you transactional messages (e.g. account, security, important changes).</li>
      </ul>
      <p>We don&rsquo;t sell your personal data. Ever.</p>

      <h2>3. Notifications &amp; reminders</h2>
      <p>
        Reminders use push notifications and only fire during the work hours you set. You
        can pause, snooze or fully turn them off at any time from inside the app or your
        device settings.
      </p>

      <h2>4. Data sharing</h2>
      <p>
        We work with a small number of trusted vendors (cloud hosting, crash reporting,
        analytics) under strict data-processing agreements. They only access what they
        need to operate Health Break on our behalf.
      </p>

      <h2>5. Data retention</h2>
      <p>
        We keep your account and wellness data for as long as your account is active. You
        can delete your account at any time from <em>Settings → Account → Delete account</em>.
        When you delete, we remove or anonymise your data within 30 days, except where we
        are legally required to keep it.
      </p>

      <h2>6. Your rights</h2>
      <ul>
        <li>Access &amp; export the data linked to your account.</li>
        <li>Correct anything that&rsquo;s inaccurate.</li>
        <li>Delete your account and the data linked to it.</li>
        <li>Object to or restrict certain processing.</li>
      </ul>
      <p>
        To exercise any of these rights, email us at{" "}
        <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>.
      </p>

      <h2>7. Children</h2>
      <p>
        Health Break is built for adults working at a desk. We don&rsquo;t knowingly
        collect data from children under 13 (or the equivalent minimum age in your
        country).
      </p>

      <h2>8. Changes to this policy</h2>
      <p>
        We may update this Privacy Policy from time to time. If changes are significant,
        we&rsquo;ll notify you in-app or by email before they take effect.
      </p>

      <h2>9. Contact us</h2>
      <p>
        Questions? Reach out at{" "}
        <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>. We aim to reply within
        two working days.
      </p>
    </>
  );
}

function TrContent() {
  return (
    <>
      <p>
        Bu Gizlilik Politikası; Health Break&rsquo;in (&ldquo;biz&rdquo;,
        &ldquo;bize&rdquo;, &ldquo;bizim&rdquo;) Health Break mobil uygulamasını ve
        ilgili hizmetleri (&ldquo;Hizmet&rdquo;) kullandığınızda bilgilerinizi nasıl
        topladığını, kullandığını ve koruduğunu açıklar. Health Break&rsquo;i
        kullanarak bu politikayı kabul etmiş olursunuz. Kabul etmiyorsanız lütfen
        uygulamayı kullanmayın.
      </p>

      <h2>1. Topladığımız bilgiler</h2>
      <h3>Hesap bilgileri</h3>
      <p>
        Apple, Google veya e-posta + şifre ile kayıt olduğunuzda hesabınızı oluşturmak
        için temel bir kimlik bilgisi (mümkün olduğunda ad ve e-posta) toplarız.
        Apple ile giriş yapıyorsanız gerçek e-postanızı gizleyebilir, Apple yönlendirme
        adresi sağlayabilir — biz onu da aynı şekilde işleriz.
      </p>
      <h3>Wellness tercihleri</h3>
      <ul>
        <li>Kurulum sırasında belirlediğiniz çalışma saatleri, odak blokları ve mola tercihleri.</li>
        <li>Seçtiğiniz vücut bölgeleri (örn. boyun, bel, bilek).</li>
        <li>Tamamladığınız, atladığınız veya tekrar oynattığınız seanslar.</li>
        <li>Gün içinde masa başında geçirdiğiniz süre.</li>
      </ul>
      <h3>Cihaz bilgileri</h3>
      <p>
        Uygulamanın stabil çalışması için gereken minimum teknik veriyi toplarız: cihaz
        tipi, işletim sistemi sürümü, uygulama sürümü, dil ve yaklaşık saat dilimi.
      </p>

      <h2>2. Bilgilerinizi nasıl kullanırız</h2>
      <ul>
        <li>Çekirdek deneyimi sunmak için: zamanında 1 dakikalık molalar, hatırlatmalar ve haftalık içgörüler.</li>
        <li>Önemsediğiniz vücut bölgelerine göre seansları kişiselleştirmek için.</li>
        <li>Uygulamayı geliştirmek için — çökme analizi, hata düzeltme, yeni özellikleri önceliklendirme.</li>
        <li>Hesap, güvenlik veya önemli değişiklikler için işlemsel mesajlar göndermek için.</li>
      </ul>
      <p>Kişisel verilerinizi <strong>asla</strong> satmayız.</p>

      <h2>3. Bildirimler ve hatırlatmalar</h2>
      <p>
        Hatırlatmalar push bildirimleri kullanır ve yalnızca belirlediğiniz çalışma
        saatleri içinde devreye girer. İstediğiniz zaman uygulamadan veya cihaz
        ayarlarınızdan duraklatabilir, erteleyebilir ya da tamamen kapatabilirsiniz.
      </p>

      <h2>4. Veri paylaşımı</h2>
      <p>
        Sıkı veri işleme sözleşmeleri kapsamında, az sayıda güvenilir tedarikçiyle
        çalışırız (bulut barındırma, çökme raporlama, analitik). Bu tedarikçiler yalnızca
        Health Break&rsquo;i bizim adımıza işletmek için gerekli olan verilere erişir.
      </p>

      <h2>5. Veri saklama</h2>
      <p>
        Hesap ve wellness verilerinizi hesabınız aktif olduğu sürece saklarız. Hesabınızı
        istediğiniz zaman <em>Ayarlar → Hesap → Hesabı sil</em> menüsünden silebilirsiniz.
        Sildiğinizde, yasal saklama gerekliliği olmadığı sürece verilerinizi 30 gün
        içinde sileriz veya anonimleştiririz.
      </p>

      <h2>6. Haklarınız</h2>
      <ul>
        <li>Hesabınıza bağlı verilere erişme ve dışa aktarma.</li>
        <li>Yanlış olan bilgileri düzeltme.</li>
        <li>Hesabınızı ve ilgili verileri silme.</li>
        <li>Belirli işlemlere itiraz etme veya kısıtlama.</li>
      </ul>
      <p>
        Bu haklardan yararlanmak için bize{" "}
        <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a> üzerinden ulaşabilirsiniz.
      </p>

      <h2>7. Çocuklar</h2>
      <p>
        Health Break, masa başında çalışan yetişkinler için tasarlanmıştır. 13 yaşın
        (veya ülkenizdeki eşdeğer asgari yaşın) altındaki çocuklardan bilerek veri
        toplamayız.
      </p>

      <h2>8. Bu politikadaki değişiklikler</h2>
      <p>
        Bu Gizlilik Politikası&rsquo;nı zaman zaman güncelleyebiliriz. Önemli değişiklikler
        olduğunda, yürürlüğe girmeden önce uygulama içinde veya e-posta ile
        bilgilendiririz.
      </p>

      <h2>9. Bize ulaşın</h2>
      <p>
        Sorularınız mı var?{" "}
        <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a> üzerinden bize ulaşın.
        İki iş günü içinde dönmeyi hedefliyoruz.
      </p>
    </>
  );
}
